import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Store, User, LogOut, LayoutDashboard, Heart, Shield } from "lucide-react";
import { LogoWord } from "@/components/Logo";
import { LocationBar } from "@/components/LocationBar";
import { useAuth } from "@/context/AuthContext";
import { useBasket } from "@/context/BasketContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function Header() {
  const { user, logout } = useAuth();
  const { count } = useBasket();
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const search = (e) => {
    e.preventDefault();
    navigate(`/browse?q=${encodeURIComponent(q)}`);
  };

  return (
    <header className="sticky top-0 z-40 bg-[#FAF7F2]/90 backdrop-blur-md border-b border-[#E5DEC9]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center gap-3 md:gap-6">
        <Link to="/" data-testid="header-logo" className="shrink-0"><LogoWord /></Link>

        <form onSubmit={search} className="hidden md:flex flex-1 max-w-md relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9A9282]" />
          <input data-testid="header-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="What are you looking for?"
            className="w-full pl-11 pr-4 py-2.5 rounded-full bg-white border border-[#E5DEC9] text-sm focus:outline-none focus:ring-2 focus:ring-[#E07A5F]/50" />
        </form>

        <div className="hidden md:block"><LocationBar /></div>

        <div className="flex items-center gap-1.5 md:gap-3 ml-auto">
          <Link to="/browse" data-testid="nav-browse" className="hidden md:block text-sm font-semibold text-[#22252A] hover:text-[#E07A5F] transition-colors">Browse</Link>
          <Link to="/sell" data-testid="nav-sell" className="hidden md:block text-sm font-semibold text-[#22252A] hover:text-[#E07A5F] transition-colors">Sell on Nabor</Link>

          <Link to="/basket" data-testid="nav-basket" className="relative p-2 rounded-full hover:bg-[#1A3C2A]/5 transition-colors">
            <ShoppingBag size={21} className="text-[#1A3C2A]" />
            {count > 0 && (
              <span data-testid="basket-count" className="absolute -top-0.5 -right-0.5 bg-[#E07A5F] text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">{count}</span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="account-menu-trigger" className="flex items-center gap-2 p-1 pr-1 md:pr-3 rounded-full hover:bg-[#1A3C2A]/5">
                  <div className="w-8 h-8 rounded-full bg-[#1A3C2A] text-white flex items-center justify-center text-sm font-bold">
                    {(user.name || "U")[0].toUpperCase()}
                  </div>
                  <span className="hidden md:block text-sm font-semibold text-[#22252A] max-w-[90px] truncate">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 rounded-2xl">
                <DropdownMenuItem onClick={() => navigate("/account")} data-testid="menu-account"><User size={15} className="mr-2" /> My Account</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-orders"><ShoppingBag size={15} className="mr-2" /> My Orders</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/favourites")} data-testid="menu-favourites"><Heart size={15} className="mr-2" /> Favourites</DropdownMenuItem>
                {user.role === "seller" && <DropdownMenuItem onClick={() => navigate("/seller")} data-testid="menu-seller"><LayoutDashboard size={15} className="mr-2" /> Seller Dashboard</DropdownMenuItem>}
                {user.role === "admin" && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><Shield size={15} className="mr-2" /> Admin Dashboard</DropdownMenuItem>}
                {user.role === "customer" && <DropdownMenuItem onClick={() => navigate("/sell")} data-testid="menu-start-selling"><Store size={15} className="mr-2" /> Start Selling</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout"><LogOut size={15} className="mr-2" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button data-testid="nav-signin" onClick={() => navigate("/auth")}
              className="rounded-full bg-[#1A3C2A] hover:bg-[#132E20] text-white px-4 md:px-5 h-9 md:h-10 text-sm font-semibold">
              Sign in
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
